import React, { useState } from "react";
import { http } from "../../lib/api";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import { Calendar, Clock, MapPin, Navigation, TrendingUp, AlertCircle } from "lucide-react";
import "leaflet/dist/leaflet.css";

export default function SimuladorRutaMes({ clientes }) {
  // Usar _id o id según lo que esté disponible
  const getClienteId = (c) => c.id || c._id;
  
  const [seleccionados, setSeleccionados] = useState(clientes.map(c => getClienteId(c)));
  const [configuracion, setConfiguracion] = useState({
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear(),
    visitas_por_semana: 2
  });
  const [ruta, setRuta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vistaActual, setVistaActual] = useState("semanas"); // "ruta" o "semanas"

  const handleChange = (id, checked) => {
    setSeleccionados(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  };

  const calcularRuta = async () => {
    setLoading(true);
    setError("");
    setRuta(null);
    try {
      const payload = {
        clientes: seleccionados,
        mes: configuracion.mes,
        anio: configuracion.anio,
        visitas_por_semana: configuracion.visitas_por_semana
      };
      console.log("📤 Enviando a /rutas/simular:", payload);
      const res = await http.post("/rutas/simular", payload);
      console.log("✅ Respuesta:", res.data);
      setRuta(res.data);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object') {
        setError(errorDetail.message || JSON.stringify(errorDetail));
      } else {
        setError(errorDetail || err.message || "Error al calcular ruta");
      }
      console.error("❌ Error detalle /rutas/simular:", err.response?.data, err);
    } finally {
      setLoading(false);
    }
  };

  const mesesNombres = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Navigation className="w-6 h-6 text-emerald-600" />
          Simulador de Rutas Mensuales
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Genera rutas optimizadas respetando horarios laborales y excluyendo sucursales remotas
        </p>
      </div>

      {/* Configuración */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-green-600" />
          Configuración
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={configuracion.mes}
              onChange={e => setConfiguracion({...configuracion, mes: parseInt(e.target.value)})}
            >
              {mesesNombres.map((nombre, i) => (
                <option key={i+1} value={i+1}>{nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={configuracion.anio}
              onChange={e => setConfiguracion({...configuracion, anio: parseInt(e.target.value)})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visitas por semana</label>
            <input
              type="number"
              min="1"
              max="7"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={configuracion.visitas_por_semana}
              onChange={e => setConfiguracion({...configuracion, visitas_por_semana: parseInt(e.target.value)})}
            />
          </div>
        </div>
      </div>

      {/* Selección de clientes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">
          Clientes incluidos ({seleccionados.length}/{clientes.length})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {clientes.map(c => {
            const clienteId = getClienteId(c);
            return (
              <label key={clienteId} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={seleccionados.includes(clienteId)}
                  onChange={e => handleChange(clienteId, e.target.checked)}
                  className="rounded"
                />
                <span className="truncate">{c.nombre}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Botón calcular */}
      <button
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={calcularRuta}
        disabled={loading || seleccionados.length === 0}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            Calculando ruta óptima...
          </>
        ) : (
          <>
            <TrendingUp className="w-5 h-5" />
            Calcular ruta óptima del mes
          </>
        )}
      </button>

      {/* Error */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-900 font-medium text-sm">Error al calcular ruta</p>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Resultados */}
      {ruta && (
        <div className="mt-6">
          {/* Estadísticas */}
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total paradas</p>
                <p className="text-2xl font-bold text-gray-900">{ruta.total_paradas}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Distancia total</p>
                <p className="text-2xl font-bold text-emerald-600">{ruta.total_distancia_km} km</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Período</p>
                <p className="text-lg font-bold text-gray-900">{mesesNombres[ruta.mes - 1]} {ruta.anio}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Visitas/semana</p>
                <p className="text-2xl font-bold text-green-600">{ruta.visitas_por_semana}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            <button
              className={`px-4 py-2 font-medium transition-colors ${
                vistaActual === "semanas"
                  ? "text-emerald-600 border-b-2 border-emerald-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setVistaActual("semanas")}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Por Semanas
            </button>
            <button
              className={`px-4 py-2 font-medium transition-colors ${
                vistaActual === "ruta"
                  ? "text-emerald-600 border-b-2 border-emerald-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setVistaActual("ruta")}
            >
              <Navigation className="w-4 h-4 inline mr-1" />
              Ruta Optimizada
            </button>
          </div>

          {/* Vista por semanas */}
          {vistaActual === "semanas" && ruta.rutas_por_semana && (
            <div className="space-y-4">
              {ruta.rutas_por_semana.map((semana, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">
                    Semana {semana.semana} ({semana.inicio} al {semana.fin})
                  </h4>
                  <div className="space-y-3">
                    {semana.dias.map((dia, dIdx) => (
                      <div key={dIdx} className="border-l-4 border-emerald-500 pl-3 py-2">
                        <p className="font-medium text-gray-900">{dia.dia_nombre} - {dia.fecha}</p>
                        <ul className="mt-2 space-y-1">
                          {dia.paradas.map((parada, pIdx) => (
                            <li key={pIdx} className="text-sm text-gray-700 flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <span className="font-medium">{parada.nombre}</span>
                                {parada.tipo === "sucursal" && <span className="text-gray-500"> (Sucursal de {parada.cliente})</span>}
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                                  <Clock className="w-3 h-3" />
                                  Horario: {parada.dias_laborales_nombres?.join(", ")}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vista ruta optimizada */}
          {vistaActual === "ruta" && (
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <h4 className="font-semibold text-lg text-gray-900 mb-3">Secuencia de visitas optimizada</h4>
                <ol className="space-y-3">
                  {ruta.ruta_optimizada.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">{p.nombre}</p>
                            <p className="text-sm text-gray-600">{p.direccion}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Coordenadas: {p.lat}, {p.lon}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                                {p.tipo === "sucursal" ? "Sucursal" : "Cliente"}
                              </span>
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {p.dias_laborales_nombres?.join(", ")}
                              </span>
                            </div>
                          </div>
                          {i > 0 && (
                            <div className="text-right">
                              <p className="text-xs text-gray-500">Distancia</p>
                              <p className="text-sm font-semibold text-emerald-600">
                                {p.distancia_desde_anterior_km} km
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Mapa */}
              <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg">
                <MapContainer
                  center={ruta.ruta_optimizada.length > 0 ? [ruta.ruta_optimizada[0].lat, ruta.ruta_optimizada[0].lon] : [-25.2637, -57.5759]}
                  zoom={12}
                  scrollWheelZoom={true}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {ruta.ruta_optimizada.map((p, i) => (
                    <Marker key={i} position={[p.lat, p.lon]}>
                      <Popup>
                        <div className="text-sm">
                          <p className="font-bold">{i + 1}. {p.nombre}</p>
                          <p className="text-gray-600">{p.direccion}</p>
                          {i > 0 && <p className="text-emerald-600 text-xs mt-1">{p.distancia_desde_anterior_km} km</p>}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  <Polyline
                    positions={ruta.ruta_optimizada.map(p => [p.lat, p.lon])}
                    color="#2563eb"
                    weight={3}
                    opacity={0.7}
                  />
                </MapContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
